import CourseLineFormService, {
  CourseLineFormServiceSuccess,
} from '@/components/course-line/CourseLineFormService';
import EvaluationsTabContent from '@/components/evaluation/EvaluationsTabContent';
import ModulesTabContent from '@/components/module/ModulesTabContent';
import WorkspaceLayout from '@/components/shared/WorkspaceLayout';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton, Tabs } from '@/components/ui/daisy';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import useCourseLineWorkspace from '@/data/hook/course-line/useCourseLineWorkspace';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import { useInfiniteCache } from '@/data/hook/query/v2/useCacheManager';
import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import { courseLinesQueryKeys } from '@/data/services/querykeys';
import { CourseLine } from '@/models/CourseLine';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import { useTranslation } from 'react-i18next';

export enum CourseLineTabs {
  INFO = 'info',
  MODULES = 'modules',
  EVALUATIONS = 'evaluations',
}

export default function CourseLineWorkspace() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.workspace',
  });
  const { tree } = useTreeCourses();
  const { isLoading, courseLine } = useCourseLineWorkspace();
  const { tab, setTab } = useWorkspaceTabs<CourseLineTabs>({
    defaultTab: CourseLineTabs.INFO,
  });
  const cacheManager = useInfiniteCache(
    courseLinesQueryKeys.list()._ctx.infinity.queryKey,
  );
  const { navigate } = useCourseTreeNavigate();
  const handleSuccessUpdate = (courseLine: CourseLine) => {
    cacheManager.update(courseLine);
    tree?.current.update({
      id: mountKey({ type: 'courseLine', id: courseLine.id }),
      name: courseLine.name,
    });
  };

  const handleSuccessCreate = (courseLine: CourseLine) => {
    cacheManager.add(courseLine, { position: 'end' });
    tree?.current.insert(
      {
        id: mountKey({ type: 'courseLine', id: courseLine.id }),
        name: courseLine.name,
        type: 'courseLine',
        realId: courseLine.id,
      },
      'root',
    );
    navigate(ROUTES.ADMIN.COURSE_LINES.INFO(courseLine.id), {
      treeKey: mountKey({ type: 'courseLine', id: courseLine.id }),
    });
  };

  const handleSuccess = (response: CourseLineFormServiceSuccess) => {
    if (response.action === 'update') handleSuccessUpdate(response.item);
    else handleSuccessCreate(response.item);
  };

  if (isLoading) return <LoadingView />;
  return (
    <WorkspaceLayout
      title={t('course')}
      subtitle={courseLine?.name || t('newCourse')}
    >
      <Tabs
        variant="box"
        className="w-fit"
        value={tab}
        onChange={value => setTab(value as CourseLineTabs)}
      >
        <Tabs.Tab value={CourseLineTabs.INFO}>{t('tabs.information')}</Tabs.Tab>
        <Tabs.Tab value={CourseLineTabs.MODULES} disabled={!courseLine}>
          {t('tabs.modules')}
        </Tabs.Tab>
        <Tabs.Tab value={CourseLineTabs.EVALUATIONS} disabled={!courseLine}>
          {t('tabs.evaluations')}
        </Tabs.Tab>
      </Tabs>
      <ConditionalRenderer condition={tab === CourseLineTabs.INFO}>
        <CourseLineFormService key={courseLine?.id} onSuccess={handleSuccess} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={tab === CourseLineTabs.MODULES && !!courseLine}
      >
        {courseLine && <ModulesTabContent courseLineId={courseLine.id} />}
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={tab === CourseLineTabs.EVALUATIONS && !!courseLine}
      >
        {courseLine && <EvaluationsTabContent courseLineId={courseLine.id} />}
      </ConditionalRenderer>
    </WorkspaceLayout>
  );
}

const LoadingView = () => {
  return (
    <div className="flex flex-col flex-1 rounded-2xl p-6 gap-6 bg-base-100">
      <div className="flex flex-col gap-5">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-8 w-40" />
      </div>
      <Skeleton className="h-12 w-52" />
      <Skeleton className="h-96 w-full" />
      <div className="flex justify-end">
        <Skeleton className="h-12 w-32" />
      </div>
    </div>
  );
};
